import { useCallback, useState } from 'react';
import { ncfAPI } from '../../services/api';
import Typeahead from '../Typeahead';

function NcfJobDivaPanel() {
  const [job, setJob] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationStage, setGenerationStage] = useState('');
  const [candidateLookupDisabled, setCandidateLookupDisabled] = useState(false);
  const [error, setError] = useState('');
  const [attachedCandidates, setAttachedCandidates] = useState([]);
  const [attachedCandidatesLoading, setAttachedCandidatesLoading] = useState(false);
  const [attachedJobs, setAttachedJobs] = useState([]);
  const [attachedJobsLoading, setAttachedJobsLoading] = useState(false);

  const toJobOption = (item) => ({
    id: String(item.id),
    label: item.title || `Job ${item.id}`,
    sublabel: [item.company, item.location, item.reference].filter(Boolean).join(' • '),
    raw: item
  });

  // JobDiva returns candidates from three different reports, each with its own field names.
  const readCandidateEmail = (item) => (
    item?.email || item?.contact?.email || item?.raw?.email || item?.raw?.EMAIL || ''
  );

  const toCandidateOption = (item) => {
    const name = item.fullName
      || item.name
      || [item.firstName, item.lastName].filter(Boolean).join(' ')
      || `Candidate ${item.id}`;
    const email = readCandidateEmail(item);
    const meta = [item.title, item.status, item.city || item.location].filter(Boolean).join(' • ');

    return {
      id: String(item.id),
      label: name,
      sublabel: [email, meta].filter(Boolean).join(' • '),
      email,
      meta,
      raw: item
    };
  };

  const fetchJobs = useCallback(async (query, { signal } = {}) => {
    const { data } = await ncfAPI.searchJobs(query, 15, { signal });
    return (data.jobs || []).map(toJobOption);
  }, []);

  const handleSelectJob = useCallback(async (selectedJob) => {
    // The candidate is deliberately left intact so a candidate-first selection survives.
    setJob(selectedJob);
    setAttachedCandidates([]);

    if (!selectedJob?.id || candidate?.id) return;

   
    setAttachedCandidatesLoading(true);
  
    try {
      const { data } = await ncfAPI.getJobCandidates(selectedJob.id);
      setAttachedCandidates((data.candidates || []).map(toCandidateOption));
    } catch {
      // Attached-candidate lookup is a convenience; manual search below still works.
      setAttachedCandidates([]);
    } finally {
      setAttachedCandidatesLoading(false);
    }
   
  }, [candidate?.id]);
    

  const handleSelectCandidate = useCallback(async (selectedCandidate) => {
    setCandidate(selectedCandidate);
    setAttachedJobs([]);

    if (!selectedCandidate?.id) return;

    setAttachedJobsLoading(true);
    try {
      const { data } = await ncfAPI.getCandidateJobs(selectedCandidate.id);
      setAttachedJobs((data.jobs || []).map(toJobOption));
    } catch {
      // Submitted-job lookup is a convenience; manual search above still works.
      setAttachedJobs([]);
    } finally {
      setAttachedJobsLoading(false);
    }
  }, []);

  const fetchCandidates = useCallback(async (query, { signal } = {}) => {
    try {
      const { data } = await ncfAPI.searchCandidates(query, 15, { signal });
      return (data.candidates || []).map(toCandidateOption);
    } catch (err) {
      if (err?.response?.data?.code === 'JOBDIVA_CANDIDATES_NOT_CONFIGURED') {
        setCandidateLookupDisabled(true);
        return [];
      }
      throw err;
    }
  }, []);


  const generateFromJobDiva = async () => {
    if (!job || !candidate) {
      setError('Select both a JobDiva job and candidate before generating the NCF.');
      return;
    }

    const jobData = job.raw || {};
    const candidateData = candidate.raw || {};
    const email = candidate.email || readCandidateEmail(candidateData);
    const payRate = jobData.payRate ?? jobData.pay_rate ?? jobData['pay rate'] ?? jobData.minRate;
    const desiredStartDate = jobData.startDate || jobData.start_date;

    const missing = [
      !email && 'candidate email',
      !payRate && 'pay rate',
      !desiredStartDate && 'start date'
    ].filter(Boolean);
    if (missing.length > 0) {
      setError(`The selected JobDiva records are missing ${missing.join(', ')}. Complete those fields in Manual Upload before generating.`);
      return;
    }

    setGenerating(true);
    setGenerationStage('Creating NCF');
    setError('');
    try {
      setGenerationStage('Loading candidate details');
      const hydrated = await ncfAPI.getCandidate(candidate.id);
      const selectedCandidate = hydrated.data.candidate || candidateData;
      const formData = new FormData();
      formData.append('candidateEmail', email);
      formData.append('payRate', payRate);
      formData.append('desiredStartDate', desiredStartDate);
      formData.append('payRateType', jobData.payRateType || 'hourly');
      formData.append('candidateFirstName', selectedCandidate.firstName || candidateData.firstName || '');
      formData.append('candidateLastName', selectedCandidate.lastName || candidateData.lastName || '');
      formData.append('clientName', jobData.company || '');
      formData.append('selectedJob', JSON.stringify(jobData));
      formData.append('selectedCandidate', JSON.stringify(selectedCandidate));

      setGenerationStage('Generating resume');
      const response = await ncfAPI.create(formData);
      setGenerationStage('Finalizing documents');
      const ncfId = response.data.ncf?.ncfId || response.data.ncf?.NcfId;
      window.location.href = `/ncfs/${ncfId}`;
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not generate the NCF from JobDiva.');
    } finally {
      setGenerating(false);
      setGenerationStage('');
    }
  };

  const canGenerate = Boolean(job && candidate);

  return (
    <section className="content-section ncf-workspace-panel">
      <div className="section-heading">
        <div><h2>Start from JobDiva</h2></div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="ncf-picker-grid">
        {!candidateLookupDisabled && (
          <div className="form-group">
            <label>JobDiva candidate</label>
            {job && attachedCandidatesLoading && <p className="form-hint">Checking JobDiva for candidates already submitted to this job...</p>}
            {job && !attachedCandidatesLoading && attachedCandidates.length > 0 && (
              <div className="ncf-attached-list">
                <p className="form-hint">Candidates submitted to this job:</p>
                <ul>
                  {attachedCandidates.map(item => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`ncf-attached-option${candidate?.id === item.id ? ' is-selected' : ''}`}
                        onClick={() => handleSelectCandidate(item)}
                      >
                        <span className="ncf-attached-option-title">{item.label}</span>
                        {item.sublabel && <span className="ncf-attached-option-meta">{item.sublabel}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Typeahead value={candidate} onSelect={handleSelectCandidate} fetchOptions={fetchCandidates} minChars={3} placeholder="Search candidates by name or email..." />
          </div>
        )}
        <div className="form-group">
          <label>JobDiva job</label>
          {candidate && attachedJobsLoading && <p className="form-hint">Checking JobDiva for jobs this candidate was submitted to...</p>}
          {candidate && !attachedJobsLoading && attachedJobs.length > 0 && (
            <div className="ncf-attached-list">
              <p className="form-hint">Jobs this candidate was submitted to:</p>
              <ul>
                {attachedJobs.map(item => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`ncf-attached-option${job?.id === item.id ? ' is-selected' : ''}`}
                      onClick={() => handleSelectJob(item)}
                    >
                      <span className="ncf-attached-option-title">{item.label}</span>
                      <span className="ncf-attached-option-meta">
                        {[item.sublabel, item.raw?.submittalStatus].filter(Boolean).join(' • ')}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Typeahead value={job} onSelect={handleSelectJob} fetchOptions={fetchJobs} minChars={2} placeholder="Search by job title or job ID..." />
        </div>
      </div>
      {candidateLookupDisabled && <p className="form-hint">Candidate lookup is not configured. You can still select a job and enter candidate details manually.</p>}
      <div className="ncf-selection-review">
        <div className={`ncf-selection-card${candidate ? ' is-selected' : ''}`}>
          <span className="ncf-selection-card-label">Candidate</span>
          {candidate ? (
            <div className="ncf-selection-card-body">
              <strong>{candidate.label}</strong>
              <span className="ncf-selection-card-detail">{candidate.email || 'No email on file in JobDiva'}</span>
              <span className="ncf-selection-card-meta">
                {[`Candidate ID ${candidate.id}`, candidate.meta].filter(Boolean).join(' • ')}
              </span>
            </div>
          ) : <span className="ncf-selection-card-empty">No candidate selected yet</span>}
        </div>
        <div className={`ncf-selection-card${job ? ' is-selected' : ''}`}>
          <span className="ncf-selection-card-label">Job</span>
          {job ? (
            <div className="ncf-selection-card-body">
              <strong>{job.label}</strong>
              <span className="ncf-selection-card-detail">{job.raw?.company || 'No client on the job record'}</span>
              <span className="ncf-selection-card-meta">
                {[
                  `Job ID ${job.id}`,
                  job.raw?.reference && `Ref ${job.raw.reference}`,
                  job.raw?.location,
                  job.raw?.submittalStatus
                ].filter(Boolean).join(' • ')}
              </span>
            </div>
          ) : <span className="ncf-selection-card-empty">No job selected yet</span>}
        </div>
      </div>
      <div className="ncf-selection-summary">
        <strong>{canGenerate ? 'Ready to generate' : 'Select a candidate and a job to continue'}</strong>
        {canGenerate && <span className="form-hint">{candidate.label} → {job.label}</span>}
        {generating && <div className="ncf-generation-progress"><span className="ncf-generation-spinner" aria-hidden="true" /><span>{generationStage}...</span></div>}
        <button type="button" className="btn btn-primary" onClick={generateFromJobDiva} disabled={!canGenerate || generating}>
          {generating ? generationStage : 'Generate NCF from JobDiva'}
        </button>
      </div>
    </section>
  );
}

export default NcfJobDivaPanel;
